import { useEffect, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { AdminOrg, OrgDeletionPreview, OrgDeletionResult } from '../../api/types';
import { Modal } from '../../components/ui/modal';

/**
 * **Delete an organization and everything it holds** (Privacy Policy §13): a
 * dry run first, then the org's name typed to confirm. The server keeps no
 * tombstone, so nothing about this org can be brought back from the app.
 *
 * The box opens on the server's own count (`GET /admin/orgs/:id/deletion`),
 * so what the operator agrees to is what the delete is about to remove, and it
 * stays open on the result: the accounts it deleted are the Auth0 logins left
 * to remove by hand.
 */
export function DeleteOrgModal({
  org,
  onClose,
  onDeleted,
}: {
  org: AdminOrg;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [preview, setPreview] = useState<OrgDeletionPreview | null>(null);
  const [result, setResult] = useState<OrgDeletionResult | null>(null);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .deletionPreview(org.id)
      .then((answer) => {
        if (live) setPreview(answer);
      })
      .catch((caught: unknown) => {
        if (live) setError(errorLine(caught));
      });
    return () => {
      live = false;
    };
  }, [org.id]);

  const close = () => {
    if (deleting) return;
    if (result) onDeleted();
    else onClose();
  };

  return (
    <Modal label="Delete organization" labelledBy="delete-org-title" onClose={close}>
      <div className="modal-head" id="delete-org-title">
        Delete organization
      </div>
      <DeleteOrgBody
        orgName={org.name}
        preview={preview}
        result={result}
        typed={typed}
        deleting={deleting}
        error={error}
        onType={setTyped}
        onClose={close}
        onDelete={async () => {
          setDeleting(true);
          setError(null);
          try {
            setResult(await api.deleteOrg(org.id, typed));
          } catch (caught) {
            setError(errorLine(caught));
          } finally {
            setDeleting(false);
          }
        }}
      />
    </Modal>
  );
}

/** The box's contents with the requests taken out, so every state renders from a fixture. */
export function DeleteOrgBody({
  orgName,
  preview,
  result,
  typed,
  deleting,
  error,
  onType,
  onClose,
  onDelete,
}: {
  orgName: string;
  preview: OrgDeletionPreview | null;
  result: OrgDeletionResult | null;
  typed: string;
  deleting: boolean;
  error: string | null;
  onType: (value: string) => void;
  onClose: () => void;
  onDelete: () => void | Promise<void>;
}) {
  const errorFlag = error ? (
    <div className="flag stopped row">
      <div className="label">Not deleted</div>
      <div className="detail">{error}</div>
    </div>
  ) : null;

  if (result) {
    return (
      <div className="form">
        <div className="flag row">
          <div className="label">Deleted</div>
          <div className="detail">
            {orgName} is gone: {plural(result.total_rows, 'row')} and{' '}
            {plural(result.storage_objects_removed, 'stored file')} removed.
          </div>
        </div>
        {result.users_deleted.length > 0 ? (
          <div className="field">
            <div className="label">Accounts deleted</div>
            <div className="field-note">Their Auth0 logins still exist. Remove them in Auth0.</div>
            <EmailList emails={result.users_deleted} />
          </div>
        ) : null}
        {result.users_kept.length > 0 ? (
          <div className="field">
            <div className="label">Accounts kept</div>
            <div className="field-note">
              Another organization&apos;s records still name these people, so their accounts stay.
            </div>
            <EmailList emails={result.users_kept} />
          </div>
        ) : null}
        <div className="modal-actions">
          <button type="button" className="btn act" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="form">
        {errorFlag ?? <div className="field-note">Counting what would be removed.</div>}
        <div className="modal-actions">
          <button type="button" className="btn act" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const blocked = preview.org.is_demo || preview.active_runs > 0;
  const confirmed = typed.trim() === preview.org.name.trim();

  return (
    <div className="form">
      <div className="field-note">
        This permanently deletes {preview.org.name} and everything it holds:{' '}
        {plural(preview.total_rows, 'row')} across {plural(preview.tables.length, 'table')}, and{' '}
        {plural(preview.storage_objects, 'stored file')} no other organization uses. It cannot be
        undone from the app. Database backups age out within 14 days.
      </div>

      <div className="deletion-tables">
        <table className="table">
          <thead>
            <tr>
              <th>Table</th>
              <th>Rows</th>
            </tr>
          </thead>
          <tbody>
            {preview.tables.map((row) => (
              <tr key={row.table}>
                <td>{row.table}</td>
                <td>{row.rows.toLocaleString('en-US')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview.users_without_other_orgs.length > 0 ? (
        <div className="field">
          <div className="label">Accounts deleted with it</div>
          <div className="field-note">
            These members belong to no other organization. An account another organization&apos;s
            records still name is kept.
          </div>
          <EmailList emails={preview.users_without_other_orgs} />
        </div>
      ) : null}

      {preview.org.is_demo ? (
        <div className="flag stopped row">
          <div className="label">Demo</div>
          <div className="detail">Demo organizations are rebuilt, not deleted.</div>
        </div>
      ) : null}
      {preview.active_runs > 0 ? (
        <div className="flag stopped row">
          <div className="label">Research running</div>
          <div className="detail">
            {plural(preview.active_runs, 'run')} still working. Wait for{' '}
            {preview.active_runs === 1 ? 'it' : 'them'} to finish, or cancel, then delete.
          </div>
        </div>
      ) : null}

      {blocked ? null : (
        <div className="field">
          <label className="label" htmlFor="delete-org-name">
            Type {preview.org.name} to confirm
          </label>
          <input
            id="delete-org-name"
            type="text"
            autoComplete="off"
            value={typed}
            disabled={deleting}
            onChange={(event) => onType(event.target.value)}
          />
        </div>
      )}

      {errorFlag}

      <div className="modal-actions">
        <button
          type="button"
          className="btn stop"
          disabled={blocked || !confirmed || deleting}
          onClick={() => void onDelete()}
        >
          {deleting ? 'Deleting…' : 'Delete organization'}
        </button>
        <button type="button" className="btn act" disabled={deleting} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EmailList({ emails }: { emails: readonly string[] }) {
  return (
    <ul className="email-list">
      {emails.map((email) => (
        <li key={email}>{email}</li>
      ))}
    </ul>
  );
}

const plural = (n: number, noun: string): string =>
  `${n.toLocaleString('en-US')} ${noun}${n === 1 ? '' : 's'}`;
